import React, { useMemo } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, addDays, isToday, isSameMonth } from 'date-fns';
import { SERVICE_CONFIG, WEEK_DAYS } from './constants';

export default function MonthlyCalendar({ currentMonth, shifts, activeService, onDayClick, selectedDate, onCall = {} }) {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });

  const weeks = useMemo(() => {
    const result = [];
    let day = calStart;
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) { week.push(new Date(day)); day = addDays(day, 1); }
      result.push(week);
      if (day > monthEnd && day.getDay() === 0) break;
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const shiftsByDate = useMemo(() => {
    const map = {};
    shifts.forEach(s => {
      if (!map[s.date]) map[s.date] = [];
      map[s.date].push(s);
    });
    return map;
  }, [shifts]);

  const getCountsForDate = (dateStr) => {
    const dayShifts = shiftsByDate[dateStr] || [];
    if (activeService !== 'all') return { total: dayShifts.filter(s => s.service_line === activeService).length };
    return {
      total: dayShifts.length,
      day_program: dayShifts.filter(s => s.service_line === 'day_program').length,
      employment: dayShifts.filter(s => s.service_line === 'employment').length,
      in_home: dayShifts.filter(s => s.service_line === 'in_home').length,
    };
  };

  return (
    <div className="border border-[#DED8EB] rounded-xl overflow-hidden bg-white shadow-sm" data-testid="monthly-calendar">
      {/* Header */}
      <div className="grid grid-cols-7 bg-[#2D1B4E]">
        {WEEK_DAYS.map(d => (
          <div key={d} className="px-2 py-2.5 text-center text-xs font-semibold text-white/70 uppercase tracking-wider">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      {weeks.map((week) => (
        <div key={format(week[0], 'yyyy-MM-dd')} className="grid grid-cols-7 border-b border-[#DED8EB] last:border-b-0">
          {week.map(date => {
            const inMonth = isSameMonth(date, currentMonth);
            const today = isToday(date);
            const dateStr = format(date, 'yyyy-MM-dd');
            const isSelected = dateStr === selectedDate;
            const counts = getCountsForDate(dateStr);
            const hasShifts = counts.total > 0;

            return (
              <button key={date.toISOString()} onClick={() => onDayClick(date)}
                className={`min-h-[90px] p-2 text-left border-r border-[#DED8EB] last:border-r-0 transition-all duration-150 group relative ${
                  isSelected ? 'bg-[#6B3FA0]/5 ring-2 ring-inset ring-[#6B3FA0] z-10' :
                  !inMonth ? 'bg-[#F7F4EF]/40 opacity-35' :
                  today ? 'bg-[#EDE7F6]/40' : 'bg-white hover:bg-[#F7F4EF]'
                }`} data-testid={`month-day-${dateStr}`}>
                {/* Date number */}
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full transition-colors ${
                    isSelected ? 'bg-[#6B3FA0] text-white' :
                    today ? 'bg-[#6B3FA0] text-white' :
                    inMonth ? 'text-[#2D1B4E] group-hover:text-[#6B3FA0]' : 'text-[#8E85A0]'
                  }`}>
                    {format(date, 'd')}
                  </span>
                  {hasShifts && (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                      isSelected ? 'bg-[#6B3FA0] text-white' : 'bg-[#F0EDF6] text-[#5A4D6E]'
                    }`}>
                      {counts.total}
                    </span>
                  )}
                </div>

                {/* Service breakdown */}
                {hasShifts && inMonth && (
                  <div className="space-y-1">
                    {activeService === 'all' ? (
                      <>
                        {counts.day_program > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-sm bg-[#9B7ED8] shrink-0" />
                            <span className="text-[10px] text-[#5A4D6E] font-medium truncate">{counts.day_program} DP</span>
                          </div>
                        )}
                        {counts.employment > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-sm bg-[#FFB74D] shrink-0" />
                            <span className="text-[10px] text-[#5A4D6E] font-medium truncate">{counts.employment} Emp</span>
                          </div>
                        )}
                        {counts.in_home > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-sm bg-[#81C784] shrink-0" />
                            <span className="text-[10px] text-[#5A4D6E] font-medium truncate">{counts.in_home} IH</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-sm ${SERVICE_CONFIG[activeService]?.dotColor?.replace('bg-', 'bg-') || 'bg-gray-400'}`} />
                        <span className="text-[10px] font-medium text-[#5A4D6E]">{counts.total} shifts</span>
                      </div>
                    )}
                  </div>
                )}

                {/* On-call staffer chip */}
                {inMonth && onCall[dateStr] && (
                  <div className="mt-1 flex items-center gap-1 rounded bg-amber-50 border border-amber-200 px-1 py-0.5" data-testid={`month-oncall-${dateStr}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                    <span className="text-[9px] font-semibold text-amber-900 truncate">OC: {onCall[dateStr].employee_name}</span>
                  </div>
                )}

                {/* Capacity bar for "all" view */}
                {hasShifts && inMonth && activeService === 'all' && (
                  <div className="mt-1.5 flex gap-0.5">
                    {counts.day_program > 0 && <div className="h-1 rounded-full bg-[#9B7ED8] flex-1" style={{ flex: counts.day_program }} />}
                    {counts.employment > 0 && <div className="h-1 rounded-full bg-[#FFB74D] flex-1" style={{ flex: counts.employment }} />}
                    {counts.in_home > 0 && <div className="h-1 rounded-full bg-[#81C784] flex-1" style={{ flex: counts.in_home }} />}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
